// Em que coluna do quadro de Faturas e Cobranças a fatura aparece.
//
// Para quase todas é o próprio `status_fatura`. A exceção é a fatura de agrupamento
// (Prefeitura de Morrinhos, coluna `cobranca_agrupada`): depois de calculada ela é tratada
// em Agrupamentos de Faturamento, e o status dela não anda mais — nenhuma rotina do
// fechamento o muda, e a trava do banco recusa mudar depois que o mês fecha. Sem esta
// regra, ela ficava para sempre em "Com boleto calculado" (ou, se pulada, em "A enviar"),
// enchendo o quadro de cards que ninguém precisa tocar.
//
// Regra (pedidos do Felipe, 19/10/2026, #6340):
//   - "Fatura disponível" e "Fatura validada" continuam no quadro: é ali que se valida e calcula.
//   - "Com boleto calculado" e "Com boleto emitido / A enviar" não mostram essas faturas.
//   - Com o malote da Secretaria entregue, as faturas dela (das UCs e o boleto agrupado) viram
//     UM card da Secretaria em "Cobrança enviada", no lugar de um card por UC.

export const COLUNAS_DO_QUADRO = [
  'Fatura disponível',
  'Fatura validada',
  'Com boleto calculado',
  'Com boleto emitido / A enviar',
  'Cobrança enviada',
] as const;

/** Coluna em que aparecem os cards das Secretarias com malote entregue. */
export const COLUNA_DOS_MALOTES = 'Cobrança enviada';

/** Etapas que não mostram fatura de agrupamento. */
const ETAPAS_DO_AGRUPAMENTO = new Set<string>(['Com boleto calculado', 'Com boleto emitido / A enviar']);

export interface FaturaNoQuadro {
  id: number | string;
  status_fatura?: string | null;
  cobranca_agrupada?: string | null;
}

/** Fechamento do mês de uma Secretaria, como vem de `agrupamento_fechamentos`. */
export interface FechamentoLido {
  id: number;
  agrupamento_id: number;
  /** Primeiro dia da competência, "2026-09-01". */
  competencia: string | null;
  fatura_boleto_id?: number | null;
  ucs_no_grupo?: number | null;
  valor_cobrado?: number | string | null;
  total_vigor?: number | string | null;
  malote_entregue_em: string | null;
  situacao_pagamento?: string | null;
}

/** Linha de `agrupamento_fechamento_faturas`: a foto das faturas que entraram no fechamento. */
export interface ItemDeFechamento {
  fechamento_id: number;
  fatura_id?: number | null;
  valor_vigor?: number | string | null;
}

export interface AgrupamentoLido {
  id: number;
  nome?: string | null;
  pagador_nome?: string | null;
}

/** O card de uma Secretaria com o malote do mês entregue. */
export interface MaloteNoQuadro {
  fechamentoId: number;
  agrupamentoId: number;
  agrupamentoNome: string;
  pagadorNome: string | null;
  /** Primeiro dia da competência, "2026-09-01". */
  competencia: string | null;
  /** UCs que entraram no fechamento (inclusive as puladas). */
  ucs: number;
  /** Dessas, as que têm valor a cobrar. */
  ucsComValor: number;
  /** Valor do boleto agrupado; null quando o fechamento não guardou valor. */
  valorCobrado: number | null;
  faturaBoletoId: number | null;
  maloteEntregueEm: string;
  situacaoPagamento: string | null;
}

export interface MalotesEntregues {
  /** Fatura (da UC ou o boleto agrupado) → fechamento cujo malote já foi entregue. */
  fechamentoDaFatura: ReadonlyMap<number, number>;
  malotes: ReadonlyMap<number, MaloteNoQuadro>;
}

export const SEM_MALOTES: MalotesEntregues = { fechamentoDaFatura: new Map(), malotes: new Map() };

const numero = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Junta o que foi lido do banco. Fechamento sem malote entregue é ignorado. */
export function montarMalotesEntregues(
  fechamentos: FechamentoLido[],
  itens: ItemDeFechamento[],
  agrupamentos: AgrupamentoLido[],
): MalotesEntregues {
  const nomes = new Map(agrupamentos.map(a => [Number(a.id), a]));
  const fechamentoDaFatura = new Map<number, number>();
  const malotes = new Map<number, MaloteNoQuadro>();

  for (const f of fechamentos) {
    if (!f.malote_entregue_em) continue;
    const ag = nomes.get(Number(f.agrupamento_id));
    malotes.set(Number(f.id), {
      fechamentoId: Number(f.id),
      agrupamentoId: Number(f.agrupamento_id),
      agrupamentoNome: (ag?.nome ?? '').trim() || `Agrupamento ${f.agrupamento_id}`,
      pagadorNome: (ag?.pagador_nome ?? '').trim() || null,
      competencia: f.competencia,
      ucs: 0,
      ucsComValor: 0,
      valorCobrado: numero(f.valor_cobrado) ?? numero(f.total_vigor),
      faturaBoletoId: f.fatura_boleto_id == null ? null : Number(f.fatura_boleto_id),
      maloteEntregueEm: f.malote_entregue_em,
      situacaoPagamento: f.situacao_pagamento ?? null,
    });
    if (f.fatura_boleto_id != null) fechamentoDaFatura.set(Number(f.fatura_boleto_id), Number(f.id));
  }

  for (const i of itens) {
    const malote = malotes.get(Number(i.fechamento_id));
    if (!malote || i.fatura_id == null) continue;
    fechamentoDaFatura.set(Number(i.fatura_id), malote.fechamentoId);
    malote.ucs += 1;
    if ((numero(i.valor_vigor) ?? 0) > 0) malote.ucsComValor += 1;
  }

  // Fechamento antigo sem a foto das faturas: usa a contagem que ele guardou.
  for (const f of fechamentos) {
    const malote = malotes.get(Number(f.id));
    if (malote && malote.ucs === 0) malote.ucs = numero(f.ucs_no_grupo) ?? 0;
  }

  return { fechamentoDaFatura, malotes };
}

/** A fatura é de agrupamento e o malote dela já foi entregue: aparece só no card da Secretaria. */
function estaNoMalote(fatura: FaturaNoQuadro, m: MalotesEntregues): boolean {
  return !!fatura.cobranca_agrupada && m.fechamentoDaFatura.has(Number(fatura.id));
}

/** Coluna do quadro em que a fatura aparece como card próprio, ou null quando fica fora. */
export function colunaDoQuadro(fatura: FaturaNoQuadro, m: MalotesEntregues): string | null {
  const status = (fatura.status_fatura ?? '').trim() || 'Fatura disponível';
  if (!fatura.cobranca_agrupada) return status;
  if (estaNoMalote(fatura, m)) return null;
  return ETAPAS_DO_AGRUPAMENTO.has(status) ? null : status;
}

/**
 * Cards de Secretaria da coluna "Cobrança enviada": um por malote entregue que tenha pelo menos
 * uma fatura na lista já filtrada (mês, busca, conta...). Assim os filtros do quadro valem para
 * eles do mesmo jeito que valem para os outros cards. Do mês mais recente para o mais antigo.
 */
export function malotesDaLista(faturas: FaturaNoQuadro[], m: MalotesEntregues): MaloteNoQuadro[] {
  const ids = new Set<number>();
  for (const f of faturas) {
    if (estaNoMalote(f, m)) ids.add(m.fechamentoDaFatura.get(Number(f.id))!);
  }
  return [...ids]
    .map(id => m.malotes.get(id)!)
    .filter(Boolean)
    .sort((a, b) =>
      (b.competencia ?? '').localeCompare(a.competencia ?? '')
      || a.agrupamentoNome.localeCompare(b.agrupamentoNome, 'pt-BR'));
}
